import {
  Box,
  Grid,
  IconButton,
  Typography,
  FormControl,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useState, useEffect, useRef } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import DeleteFileRedIcon from '../../../../../components/Icons/DeleteFileRedIcon';
import DateInput from '../../../../../components/MUIComponents/DateInput';
import {
  useGetLeagueBySportNameQuery,
  useGetTeamsByLeagueIdOrExternalIdQuery,
} from '../../../../../store/apiSlice/careerApi';
import { useGetSportsQuery } from '../../../../../store/apiSlice/sportApi';
import { withUnderScore } from '../../../../../utils/withUnderScore';
import ArrowDownIcon from '../../../../../components/Icons/ArrowDownIcon';
import AutocompleteOption from '../../../../../components/shared/AutocompleteOption/AutocompleteOption';
import { EditSportBackgroundFormInputs } from '../EditSportBackgroundForm';

interface TeamRowProps {
  control: Control<EditSportBackgroundFormInputs, any>;
  sportIndex: number;
  teamIndex: number;
  totalTeams: number;
  watch: UseFormWatch<EditSportBackgroundFormInputs>;
  setValue: UseFormSetValue<EditSportBackgroundFormInputs>;
  errors: any;
  register: any;
  trigger: any;
  onRemove: () => void;
  canRemove: boolean;
  isIndividualSport?: boolean;
}

const TeamRow = ({
  control,
  sportIndex,
  teamIndex,
  totalTeams,
  watch,
  setValue,
  errors: _errors,
  onRemove,
  canRemove,
  isIndividualSport = false,
}: TeamRowProps) => {
  const [typedLeague, setTypedLeague] = useState('');
  const [typedTeam, setTypedTeam] = useState('');
  const hasHydratedText = useRef(false);

  const sportId = watch(`sportBackgrounds.${sportIndex}.sportId` as const);
  const isOtherSport = watch(
    `sportBackgrounds.${sportIndex}.isOtherSport` as const
  );
  const sportName = watch(`sportBackgrounds.${sportIndex}.sportName` as const);

  const { data: sports = [] } = useGetSportsQuery();

  const resolvedSportName = isOtherSport
    ? (sportName as string)
    : sports.find((s: any) => s.id === sportId)?.name || (sportName as string);

  const leagueId = watch(
    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueId` as const
  );

  const isActiveTeam = watch(
    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.isActiveTeam` as const
  );

  const leagueNameValue = watch(
    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const
  );

  const teamNameValue = watch(
    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const
  );

  useEffect(() => {
    if (hasHydratedText.current) return;

    if (leagueNameValue) {
      setTypedLeague(leagueNameValue as string);
    }

    if (teamNameValue) {
      setTypedTeam(teamNameValue as string);
    }

    hasHydratedText.current = true;
  }, [leagueNameValue, teamNameValue]);

  const { data: leagues = [], isLoading: isLeagueLoading } =
    useGetLeagueBySportNameQuery(withUnderScore(resolvedSportName || ''), {
      skip: !resolvedSportName,
    });

  const { data: teams = [], isLoading: isTeamLoading } =
    useGetTeamsByLeagueIdOrExternalIdQuery(
      withUnderScore(leagueId || 'other'),
      {
        skip: !leagueId,
      }
    );

  return (
    <Grid
      item
      container
      spacing={2}
      id={`team-row-${sportIndex}-${teamIndex}`}
      sx={{ position: 'relative', pt: 3, pb: 3 }}
    >
      {canRemove && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 0,
            zIndex: 1,
          }}
        >
          <IconButton onClick={onRemove} size="small">
            <DeleteFileRedIcon />
          </IconButton>
        </Box>
      )}

      {/* League Autocomplete */}
      <Grid item xs={12} md={6}>
        <Controller
          name={
            `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueId` as const
          }
          control={control}
          render={({ field }) => (
            <FormControl
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
              variant="outlined"
              fullWidth
              error={
                !!_errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                  ?.leagueName
              }
            >
              <Typography
                variant="caption"
                sx={{
                  padding: '0 2px',
                  mb: '12px',
                  color: 'text.primary',
                }}
              >
                League:
              </Typography>
              <Autocomplete
                {...field}
                options={leagues}
                loading={isLeagueLoading}
                freeSolo
                getOptionLabel={(option: any) =>
                  typeof option === 'string' ? option : option.strLeague || ''
                }
                value={
                  leagues.find((l: any) => l.idLeague === field.value) || null
                }
                inputValue={typedLeague}
                onInputChange={(_e, newValue) => {
                  setTypedLeague(newValue);
                  setValue(
                    `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                    newValue
                  );
                }}
                onChange={(_e, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    field.onChange(newValue.idLeague);
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.leagueName` as const,
                      newValue.strLeague
                    );
                    setTypedLeague(newValue.strLeague);
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const,
                      ''
                    );
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                      ''
                    );
                    setTypedTeam('');
                  } else {
                    field.onChange('');
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const,
                      ''
                    );
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                      ''
                    );
                    setTypedTeam('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      name: `sportBackgrounds[${sportIndex}].teams[${teamIndex}].leagueName`,
                    }}
                    placeholder="Search or enter a league"
                    error={
                      !!_errors?.sportBackgrounds?.[sportIndex]?.teams?.[
                        teamIndex
                      ]?.leagueName
                    }
                    helperText={
                      _errors?.sportBackgrounds?.[sportIndex]?.teams?.[
                        teamIndex
                      ]?.leagueName?.message
                    }
                  />
                )}
                renderOption={(props, option: any) => {
                  const { key, ...otherProps } = props;
                  return (
                    <AutocompleteOption
                      key={key}
                      text={option?.strLeague || ''}
                      props={otherProps}
                    />
                  );
                }}
                popupIcon={<ArrowDownIcon />}
                sx={{
                  '.MuiInputBase-root': {
                    height: '50px',
                  },
                  '.MuiSvgIcon-root': {
                    bgcolor: 'transparent !important',
                  },
                }}
              />
            </FormControl>
          )}
        />
      </Grid>

      {/* Team Autocomplete - Hide for individual sports */}
      {!isIndividualSport && (
        <Grid item xs={12} md={6}>
          <Controller
            name={
              `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamId` as const
            }
            control={control}
            render={({ field }) => (
              <FormControl
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                variant="outlined"
                fullWidth
                error={
                  !!_errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                    ?.teamName
                }
              >
                <Typography
                  variant="caption"
                  sx={{
                    padding: '0 2px',
                    mb: '12px',
                    color: 'text.primary',
                  }}
                >
                  Team:
                </Typography>
                <Autocomplete
                  {...field}
                  options={teams}
                  loading={isTeamLoading}
                  disabled={!leagueId && !typedLeague}
                  freeSolo
                  getOptionLabel={(option: any) =>
                    typeof option === 'string' ? option : option.strTeam || ''
                  }
                  value={
                    teams.find((t: any) => t.idTeam === field.value) || null
                  }
                  inputValue={typedTeam}
                  onInputChange={(_e, newValue) => {
                    setTypedTeam(newValue);
                    setValue(
                      `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                      newValue
                    );
                  }}
                  onChange={(_e, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      field.onChange(newValue.idTeam);
                      setValue(
                        `sportBackgrounds.${sportIndex}.teams.${teamIndex}.teamName` as const,
                        newValue.strTeam
                      );
                      setTypedTeam(newValue.strTeam);
                    } else {
                      field.onChange('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        name: `sportBackgrounds[${sportIndex}].teams[${teamIndex}].teamName`,
                      }}
                      placeholder="Search or enter a team"
                      disabled={!leagueId && !typedLeague}
                      error={
                        !!_errors?.sportBackgrounds?.[sportIndex]?.teams?.[
                          teamIndex
                        ]?.teamName
                      }
                      helperText={
                        _errors?.sportBackgrounds?.[sportIndex]?.teams?.[
                          teamIndex
                        ]?.teamName?.message
                      }
                    />
                  )}
                  renderOption={(props, option: any) => {
                    const { key, ...otherProps } = props;
                    return (
                      <AutocompleteOption
                        key={key}
                        text={option?.strTeam || ''}
                        props={otherProps}
                      />
                    );
                  }}
                  popupIcon={<ArrowDownIcon />}
                  sx={{
                    '.MuiInputBase-root': {
                      height: '50px',
                    },
                    '.MuiSvgIcon-root': {
                      bgcolor: 'transparent !important',
                    },
                  }}
                />
              </FormControl>
            )}
          />
        </Grid>
      )}

      {/* Team Timeline */}
      <Grid item xs={12}>
        <Typography variant="caption" sx={{ padding: '0 2px', mb: '12px' }}>
          Timeline:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sx={{ width: '100%' }}>
            <DateInput
              control={control}
              name={
                `sportBackgrounds.${sportIndex}.teams.${teamIndex}.startDateInTeam` as const
              }
              placeholder="Your start date"
              error={
                _errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                  ?.startDateInTeam
              }
              helperText={
                _errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                  ?.startDateInTeam?.message
              }
            />
          </Grid>
          {!isActiveTeam && (
            <Grid item xs={12} md={6}>
              <DateInput
                control={control}
                name={
                  `sportBackgrounds.${sportIndex}.teams.${teamIndex}.endDateInTeam` as const
                }
                placeholder="Your end date"
                error={
                  _errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                    ?.endDateInTeam
                }
                helperText={
                  _errors?.sportBackgrounds?.[sportIndex]?.teams?.[teamIndex]
                    ?.endDateInTeam?.message
                }
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Active Team Checkbox */}
      <Grid item xs={12}>
        <Controller
          name={
            `sportBackgrounds.${sportIndex}.teams.${teamIndex}.isActiveTeam` as const
          }
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                  sx={{ padding: 0, margin: 0 }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 400, fontSize: '14px' }}
                >
                  I am currently on this team
                </Typography>
              }
              sx={{
                padding: 0,
                margin: 0,
                gap: '8px',
                width: '100%',
                alignItems: 'center',
              }}
            />
          )}
        />
      </Grid>

      {teamIndex < totalTeams - 1 && (
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>
      )}
    </Grid>
  );
};

export default TeamRow;
